import React, { useState, useRef, useEffect } from "react";
import { Doughnut, Bar, Line } from "react-chartjs-2";
import article from "./TaxifyContent";
import WaldoAd from '../AdComponent';
import { 
  Chart, 
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
} from "chart.js";

Chart.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Filler,
  BarElement
);

const NetProfitAfterTaxCalculator = () => {
  const content = article.find((item) => item.id === 5);
  const [mode, setMode] = useState("stocks");
  const [activeTab, setActiveTab] = useState("calculator");
  const [isMobile, setIsMobile] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [showSaved, setShowSaved] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [inputs, setInputs] = useState({
    // Investment Details
    initialInvestment: "10000",
    currentValue: "15000",
    investmentType: "stocks",
    
    // Trade Details
    buyPrice: "50.00",
    sellPrice: "75.00",
    shares: "200",
    purchaseDate: "2023-01-15",
    saleDate: "2024-01-15",
    
    // Income & Expenses
    dividendIncome: "500",
    interestIncome: "250",
    tradingFees: "50",
    managementFees: "100",
    otherExpenses: "75",
    
    // Tax Information
    filingStatus: "single",
    annualIncome: "85000",
    stateTaxRate: "7.5",
    country: "US",
    
    // Tax Settings
    applyStandardDeduction: true,
    applyNII: true,
    applyQBI: false,
    
    // Multiple Positions
    additionalGains: "0",
    additionalLosses: "0",
    
    // Retirement Accounts
    isRetirementAccount: false,
    retirementAccountType: "traditional"
  });

  const [result, setResult] = useState(null);
  const [calculationHistory, setCalculationHistory] = useState([]);
  const chartRef = useRef();

  // Tax brackets for 2024
  const taxBrackets = {
    single: [
      { min: 0, max: 11600, rate: 0.10 },
      { min: 11601, max: 47150, rate: 0.12 },
      { min: 47151, max: 100525, rate: 0.22 },
      { min: 100526, max: 191950, rate: 0.24 },
      { min: 191951, max: 243725, rate: 0.32 },
      { min: 243726, max: 609350, rate: 0.35 },
      { min: 609351, max: Infinity, rate: 0.37 }
    ],
    married: [
      { min: 0, max: 23200, rate: 0.10 },
      { min: 23201, max: 94300, rate: 0.12 },
      { min: 94301, max: 201050, rate: 0.22 },
      { min: 201051, max: 383900, rate: 0.24 },
      { min: 383901, max: 487450, rate: 0.32 },
      { min: 487451, max: 731200, rate: 0.35 },
      { min: 731201, max: Infinity, rate: 0.37 }
    ]
  };

  // Long-term capital gains brackets
  const ltcgBrackets = {
    single: [
      { min: 0, max: 47025, rate: 0.00 },
      { min: 47026, max: 518900, rate: 0.15 },
      { min: 518901, max: Infinity, rate: 0.20 }
    ],
    married: [
      { min: 0, max: 94050, rate: 0.00 },
      { min: 94051, max: 583750, rate: 0.15 },
      { min: 583751, max: Infinity, rate: 0.20 }
    ]
  };

  // Check mobile viewport
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-calculate when inputs change
  useEffect(() => {
    if (activeTab === "calculator") {
      calculateNetProfit();
    }
  }, [inputs, mode, activeTab]);

  const addNotification = (message, type = "info") => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(notif => notif.id !== id));
    }, 5000);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const calculateHoldingPeriod = (purchaseDate, saleDate) => {
    const purchase = new Date(purchaseDate);
    const sale = new Date(saleDate);
    const diffTime = Math.abs(sale - purchase);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const calculateNetProfit = () => {
    const {
      initialInvestment, currentValue, investmentType,
      buyPrice, sellPrice, shares, purchaseDate, saleDate,
      dividendIncome, interestIncome, tradingFees, managementFees, otherExpenses,
      filingStatus, annualIncome, stateTaxRate, applyStandardDeduction,
      applyNII, additionalGains, additionalLosses, isRetirementAccount
    } = inputs;

    // Parse inputs
    const initialInv = parseFloat(initialInvestment);
    const currentVal = parseFloat(currentValue);
    const bp = parseFloat(buyPrice);
    const sp = parseFloat(sellPrice);
    const sh = parseFloat(shares);
    const income = parseFloat(annualIncome);
    const stateRate = parseFloat(stateTaxRate) / 100;
    const dividends = parseFloat(dividendIncome || 0);
    const interest = parseFloat(interestIncome || 0);
    const fees = parseFloat(tradingFees || 0) + parseFloat(managementFees || 0) + parseFloat(otherExpenses || 0);
    const addGains = parseFloat(additionalGains || 0);
    const addLosses = parseFloat(additionalLosses || 0);

    // Calculate capital gain
    let capitalGain;
    if (investmentType === "manual") {
      capitalGain = currentVal - initialInv;
    } else {
      const totalCost = bp * sh;
      const totalProceeds = sp * sh;
      capitalGain = totalProceeds - totalCost;
    }

    // Calculate holding period
    const holdingDays = calculateHoldingPeriod(purchaseDate, saleDate);
    const isLongTerm = holdingDays > 365;

    // Total gross profit
    const grossProfit = capitalGain + dividends + interest + addGains - addLosses;
    
    // Calculate taxes
    let totalTax = 0;
    
    if (!isRetirementAccount) {
      // Capital gains tax
      const capGainsTax = calculateCapitalGainsTax(capitalGain, income, filingStatus, isLongTerm);
      
      // Dividend tax (assuming qualified)
      const dividendTax = calculateQualifiedDividendTax(dividends, income, filingStatus);
      
      // Interest tax (ordinary income)
      const interestTax = calculateOrdinaryIncomeTax(interest, income, filingStatus);
      
      // Additional gains tax
      const addGainsTax = calculateCapitalGainsTax(addGains, income, filingStatus, true);
      
      // State tax
      const stateTax = (capitalGain + dividends + interest + addGains) * stateRate;
      
      // Net Investment Income Tax
      let niit = 0;
      if (applyNII) {
        const niiThreshold = filingStatus === 'single' ? 200000 : 250000;
        const niiTaxable = Math.max(0, (income + capitalGain + dividends + interest + addGains) - niiThreshold);
        niit = niiTaxable * 0.038;
      }
      
      totalTax = capGainsTax + dividendTax + interestTax + addGainsTax + stateTax + niit;
    }

    // Net profit calculation
    const totalExpenses = fees + totalTax;
    const netProfit = grossProfit - totalExpenses;
    const netReturn = (netProfit / initialInv) * 100;
    const taxEfficiency = (1 - (totalTax / Math.max(grossProfit, 1))) * 100;

    const resultData = {
      // Basic metrics
      initialInvestment: initialInv,
      currentValue: currentVal,
      grossProfit,
      netProfit,
      totalTax,
      totalExpenses: fees,
      
      // Returns
      netReturn,
      taxEfficiency,
      holdingPeriod: holdingDays,
      isLongTerm,
      
      // Breakdown
      capitalGain,
      dividendIncome: dividends,
      interestIncome: interest,
      additionalGains: addGains,
      additionalLosses: addLosses,
      
      // Tax details
      capitalGainsTax: calculateCapitalGainsTax(capitalGain, income, filingStatus, isLongTerm),
      dividendTax: calculateQualifiedDividendTax(dividends, income, filingStatus),
      interestTax: calculateOrdinaryIncomeTax(interest, income, filingStatus),
      stateTax: (capitalGain + dividends + interest + addGains) * stateRate,
      niit: applyNII ? calculateNIIT(capitalGain + dividends + interest + addGains, income, filingStatus) : 0,
      
      // Ratios
      taxToProfitRatio: totalTax / Math.max(grossProfit, 1),
      expenseRatio: fees / Math.max(grossProfit, 1),
      
      // Advanced metrics
      annualizedReturn: calculateAnnualizedReturn(initialInv, netProfit, holdingDays),
      breakEvenAfterTax: calculateBreakEvenPoint(initialInv, totalExpenses, capitalGain)
    };

    setResult(resultData);
  };

  const calculateCapitalGainsTax = (gain, income, filingStatus, isLongTerm) => {
    if (gain <= 0) return 0;
    
    if (isLongTerm) {
      return calculateLongTermCapitalGainsTax(gain, income, filingStatus);
    } else {
      return calculateShortTermCapitalGainsTax(gain, income, filingStatus);
    }
  };

  const calculateLongTermCapitalGainsTax = (gain, income, filingStatus) => {
    const brackets = ltcgBrackets[filingStatus];
    let tax = 0;
    let remainingGain = gain;

    for (const bracket of brackets) {
      if (remainingGain <= 0) break;

      const bracketIncome = income > bracket.min ? Math.min(income, bracket.max) - bracket.min : 0;
      const taxableInBracket = Math.min(remainingGain, bracket.max - Math.max(income, bracket.min));

      if (taxableInBracket > 0) {
        tax += taxableInBracket * bracket.rate;
        remainingGain -= taxableInBracket;
      }
    }

    return tax;
  };

  const calculateShortTermCapitalGainsTax = (gain, income, filingStatus) => {
    const brackets = taxBrackets[filingStatus];
    let tax = 0;
    let remainingGain = gain;

    for (const bracket of brackets) {
      if (remainingGain <= 0) break;

      const bracketMin = Math.max(bracket.min, income);
      const bracketMax = bracket.max;
      
      if (bracketMin < bracketMax) {
        const taxableInBracket = Math.min(remainingGain, bracketMax - bracketMin);
        tax += taxableInBracket * bracket.rate;
        remainingGain -= taxableInBracket;
      }
    }

    return tax;
  };

  const calculateQualifiedDividendTax = (dividends, income, filingStatus) => {
    return calculateLongTermCapitalGainsTax(dividends, income, filingStatus);
  };

  const calculateOrdinaryIncomeTax = (incomeAmount, totalIncome, filingStatus) => {
    return calculateShortTermCapitalGainsTax(incomeAmount, totalIncome, filingStatus);
  };

  const calculateNIIT = (investmentIncome, totalIncome, filingStatus) => {
    const threshold = filingStatus === 'single' ? 200000 : 250000;
    const taxableAmount = Math.max(0, totalIncome + investmentIncome - threshold);
    return taxableAmount * 0.038;
  };

  const calculateAnnualizedReturn = (initial, profit, days) => {
    if (days === 0) return 0;
    const years = days / 365;
    const totalValue = initial + profit;
    const cagr = (Math.pow(totalValue / initial, 1 / years) - 1) * 100;
    return isFinite(cagr) ? cagr : 0;
  };

  const calculateBreakEvenPoint = (initial, expenses, gain) => {
    const requiredGain = expenses;
    const breakEvenPercent = (requiredGain / initial) * 100;
    return breakEvenPercent;
  };

  const calculateScenarioAnalysis = () => {
    if (!result) return null;

    const scenarios = [
      {
        name: "Conservative",
        returnMultiplier: 0.5,
        taxMultiplier: 1.1
      },
      {
        name: "Base Case",
        returnMultiplier: 1.0,
        taxMultiplier: 1.0
      },
      {
        name: "Optimistic",
        returnMultiplier: 1.5,
        taxMultiplier: 0.9
      },
      {
        name: "Aggressive",
        returnMultiplier: 2.0,
        taxMultiplier: 0.8
      }
    ];

    return scenarios.map(scenario => {
      const scenarioProfit = result.grossProfit * scenario.returnMultiplier;
      const scenarioTax = result.totalTax * scenario.taxMultiplier;
      const scenarioNet = scenarioProfit - scenarioTax - result.totalExpenses;
      
      return {
        name: scenario.name,
        grossProfit: scenarioProfit,
        totalTax: scenarioTax,
        netProfit: scenarioNet,
        netReturn: (scenarioNet / result.initialInvestment) * 100
      };
    });
  };

  const resetCalculator = () => {
    setInputs({
      initialInvestment: "10000",
      currentValue: "15000",
      investmentType: "stocks",
      buyPrice: "50.00",
      sellPrice: "75.00",
      shares: "200",
      purchaseDate: "2023-01-15",
      saleDate: "2024-01-15",
      dividendIncome: "500",
      interestIncome: "250",
      tradingFees: "50",
      managementFees: "100",
      otherExpenses: "75",
      filingStatus: "single",
      annualIncome: "85000",
      stateTaxRate: "7.5",
      country: "US",
      applyStandardDeduction: true,
      applyNII: true,
      applyQBI: false,
      additionalGains: "0",
      additionalLosses: "0",
      isRetirementAccount: false,
      retirementAccountType: "traditional"
    });
    addNotification("Net profit calculator reset to default values", "success");
  };

  const exportToCSV = () => {
    if (!result) return;
    
    const csvContent = [
      "Metric,Value",
      `Initial Investment,${result.initialInvestment.toFixed(2)}`,
      `Current Value,${result.currentValue.toFixed(2)}`,
      `Gross Profit,${result.grossProfit.toFixed(2)}`,
      `Total Taxes,${result.totalTax.toFixed(2)}`,
      `Total Expenses,${result.totalExpenses.toFixed(2)}`,
      `Net Profit,${result.netProfit.toFixed(2)}`,
      `Net Return,${result.netReturn.toFixed(2)}%`,
      `Tax Efficiency,${result.taxEfficiency.toFixed(2)}%`,
      `Holding Period,${result.holdingPeriod} days`,
      `Annualized Return,${result.annualizedReturn.toFixed(2)}%`
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'net-profit-calculation.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    addNotification("Net profit data exported to CSV successfully", "success");
  };

  const printResult = () => {
    window.print();
  };

  const saveToHistory = () => {
    if (!result) return;
    
    const newCalculation = {
      id: Date.now(),
      timestamp: new Date().toLocaleString(),
      inputs: { ...inputs },
      result: { ...result }
    };
    
    setCalculationHistory(prev => [newCalculation, ...prev.slice(0, 9)]);
    addNotification("Net profit calculation saved to history", "success");
  };

  const loadCalculation = (calc) => {
    setInputs(calc.inputs);
    setResult(calc.result);
    setShowSaved(false);
    addNotification("Net profit calculation loaded successfully", "success");
  };

  const quickPreset = (preset) => {
    const presets = {
      conservative: { 
        investmentType: "bonds",
        annualIncome: "50000",
        stateTaxRate: "5.0"
      },
      moderate: { 
        investmentType: "stocks",
        annualIncome: "85000",
        stateTaxRate: "7.5"
      },
      aggressive: {
        investmentType: "crypto",
        annualIncome: "150000",
        stateTaxRate: "10.0"
      }
    };
    
    setInputs(prev => ({ ...prev, ...presets[preset] }));
    addNotification(`${preset} investment preset applied`, "info");
  };

  // Chart Data
  const profitBreakdownChartData = result ? {
    labels: ["Initial Investment", "Gross Profit", "Taxes", "Expenses", "Net Profit"],
    datasets: [{
      data: [
        result.initialInvestment,
        Math.max(result.grossProfit, 0),
        Math.max(result.totalTax, 0),
        Math.max(result.totalExpenses, 0),
        Math.max(result.netProfit, 0)
      ],
      backgroundColor: ["#4B5563", "#10B981", "#EF4444", "#F59E0B", "#3B82F6"],
      borderWidth: 2,
      hoverOffset: 15
    }],
  } : null;

  const taxBreakdownChartData = result ? {
    labels: ["Capital Gains Tax", "Dividend Tax", "Interest Tax", "State Tax", "NIIT"],
    datasets: [{
      data: [
        result.capitalGainsTax,
        result.dividendTax,
        result.interestTax,
        result.stateTax,
        result.niit
      ],
      backgroundColor: ["#EF4444", "#DC2626", "#B91C1C", "#F59E0B", "#D97706"],
      borderWidth: 2
    }],
  } : null;

  const scenarioData = calculateScenarioAnalysis();
  const scenarioChartData = scenarioData ? {
    labels: scenarioData.map(s => s.name),
    datasets: [
      {
        label: 'Net Profit',
        data: scenarioData.map(s => s.netProfit),
        backgroundColor: "#10B981",
        borderColor: "#10B981",
        borderWidth: 2
      },
      {
        label: 'Gross Profit',
        data: scenarioData.map(s => s.grossProfit),
        backgroundColor: "#3B82F6",
        borderColor: "#3B82F6",
        borderWidth: 2
      }
    ]
  } : null;

  return (
    <div className={'profit-calculator'}>
      {/* Notifications */}
      <div className="notifications-container">
        {notifications.map(notif => (
          <div key={notif.id} className={`notification ${notif.type}`}>
            <i className={`fas fa-${notif.type === 'success' ? 'check' : 'info'}-circle`}></i>
            {notif.message}
          </div>
        ))}
      </div>

      <div className="calculator-header">
        <div className="header-top">
          <h1>Net Profit After Tax Calculator</h1>
        </div>
        <p>Calculate your true investment returns after accounting for taxes, fees, and expenses</p>
      </div>
<WaldoAd type="horizontal" /><br/>
      {/* Saved Calculations Modal */}
      {showSaved && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Saved Net Profit Calculations</h3>
              <button className="close-btn" onClick={() => setShowSaved(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="saved-list">
              {savedCalculations.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-bookmark fa-2x"></i>
                  <p>No saved calculations yet</p>
                </div>
              ) : (
                savedCalculations.map(calc => (
                  <div key={calc.id} className="saved-item" onClick={() => loadCalculation(calc)}>
                    <div className="saved-name">{calc.name}</div>
                    <div className="saved-details">
                      <span className={`profit ${calc.result.netProfit >= 0 ? 'positive' : 'negative'}`}>
                        ${calc.result.netProfit.toFixed(2)}
                      </span>
                      <span className="date">{calc.timestamp}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="calculator-layout">
        {/* Left Side - Inputs */}
        <div className="left-panel">
          {/* Navigation Tabs */}
          <div className="tabs-container">
            <div className="tabs">
              <button 
                className={`tab ${activeTab === "calculator" ? "active" : ""}`}
                onClick={() => setActiveTab("calculator")}
              >
                <i className="fas fa-calculator"></i>
                {isMobile ? 'Calculator' : 'Net Profit Calculator'}
              </button>
              <button 
                className={`tab ${activeTab === "scenarios" ? "active" : ""}`}
                onClick={() => setActiveTab("scenarios")}
              >
                <i className="fas fa-chart-line"></i>
                {isMobile ? 'Scenarios' : 'Scenario Analysis'}
              </button>
              <button 
                className={`tab ${activeTab === "history" ? "active" : ""}`}
                onClick={() => setActiveTab("history")}
              >
                <i className="fas fa-history"></i>
                {isMobile ? 'History' : 'History'}
              </button>
            </div>
          </div>

          {/* Quick Presets */}
          <div className="presets-section">
            <h4>Investment Profiles:</h4>
            <div className="preset-buttons">
              <button className="preset-btn conservative" onClick={() => quickPreset('conservative')}>
                Conservative
              </button>
              <button className="preset-btn moderate" onClick={() => quickPreset('moderate')}>
                Moderate
              </button>
              <button className="preset-btn aggressive" onClick={() => quickPreset('aggressive')}>
                Aggressive
              </button>
            </div>
          </div>

          {/* Calculator Tab */}
          {activeTab === "calculator" && (
            <div className="calculator-content">
              <div className="input-section">
                <div className="input-group">
                  <h3>
                    <i className="fas fa-chart-line"></i>
                    Investment Details
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill-wave"></i> Investment Type</label>
                      <select name="investmentType" value={inputs.investmentType} onChange={handleChange}>
                        <option value="stocks">Stocks</option>
                        <option value="bonds">Bonds</option>
                        <option value="crypto">Cryptocurrency</option>
                        <option value="realestate">Real Estate</option>
                        <option value="etf">ETF/Mutual Fund</option>
                        <option value="manual">Manual Input</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-dollar-sign"></i> Initial Investment</label>
                      <input 
                        type="number" 
                        name="initialInvestment" 
                        value={inputs.initialInvestment} 
                        onChange={handleChange}
                        placeholder="10000"
                      />
                    </div>
                  </div>

                  {inputs.investmentType === "manual" ? (
                    <div className="input-row">
                      <div className="input-field">
                        <label><i className="fa-solid fa-dollar-sign"></i> Current Value</label>
                        <input 
                          type="number" 
                          name="currentValue" 
                          value={inputs.currentValue} 
                          onChange={handleChange}
                          placeholder="15000"
                        />
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="input-row">
                        <div className="input-field">
                          <label><i className="fa-solid fa-dollar-sign"></i> Buy Price</label>
                          <input 
                            type="number" 
                            name="buyPrice" 
                            value={inputs.buyPrice} 
                            onChange={handleChange}
                            placeholder="50.00"
                            step="0.01"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-dollar-sign"></i> Sell Price</label>
                          <input 
                            type="number" 
                            name="sellPrice" 
                            value={inputs.sellPrice} 
                            onChange={handleChange}
                            placeholder="75.00"
                            step="0.01"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-layer-group"></i> Shares/Units</label>
                          <input 
                            type="number" 
                            name="shares" 
                            value={inputs.shares} 
                            onChange={handleChange}
                            placeholder="200"
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-calendar"></i> Purchase Date</label>
                          <input 
                            type="date" 
                            name="purchaseDate" 
                            value={inputs.purchaseDate} 
                            onChange={handleChange}
                          />
                        </div>
                        <div className="input-field">
                          <label><i className="fa-solid fa-calendar"></i> Sale Date</label>
                          <input 
                            type="date" 
                            name="saleDate" 
                            value={inputs.saleDate} 
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  <h3>
                    <i className="fas fa-money-bill"></i>
                    Income & Expenses
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-coins"></i> Dividend Income</label>
                      <input 
                        type="number" 
                        name="dividendIncome" 
                        value={inputs.dividendIncome} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> Interest Income</label>
                      <input 
                        type="number" 
                        name="interestIncome" 
                        value={inputs.interestIncome} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-receipt"></i> Trading Fees</label>
                      <input 
                        type="number" 
                        name="tradingFees" 
                        value={inputs.tradingFees} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-cog"></i> Management Fees</label>
                      <input 
                        type="number" 
                        name="managementFees" 
                        value={inputs.managementFees} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Other Expenses</label>
                      <input 
                        type="number" 
                        name="otherExpenses" 
                        value={inputs.otherExpenses} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-file-invoice-dollar"></i>
                    Tax Information
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label><i className="fa-solid fa-user"></i> Filing Status</label>
                      <select name="filingStatus" value={inputs.filingStatus} onChange={handleChange}>
                        <option value="single">Single</option>
                        <option value="married">Married Filing Jointly</option>
                      </select>
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-money-bill"></i> Annual Income</label>
                      <input 
                        type="number" 
                        name="annualIncome" 
                        value={inputs.annualIncome} 
                        onChange={handleChange}
                        placeholder="0"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-percentage"></i> State Tax Rate (%)</label>
                      <input 
                        type="number" 
                        name="stateTaxRate" 
                        value={inputs.stateTaxRate} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Additional Gains</label>
                      <input 
                        type="number" 
                        name="additionalGains" 
                        value={inputs.additionalGains} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                    <div className="input-field">
                      <label><i className="fa-solid fa-file-invoice-dollar"></i> Additional Losses</label>
                      <input 
                        type="number" 
                        name="additionalLosses" 
                        value={inputs.additionalLosses} 
                        onChange={handleChange}
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>

                  <h3>
                    <i className="fas fa-cog"></i>
                    Tax Settings
                  </h3>
                  <div className="input-row">
                    <div className="input-field">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="applyNII" 
                          checked={inputs.applyNII} 
                          onChange={handleChange}
                        />
                        Apply Net Investment Income Tax (3.8%)
                      </label>
                    </div>
                    <div className="input-field">
                      <label className="checkbox-label">
                        <input 
                          type="checkbox" 
                          name="isRetirementAccount" 
                          checked={inputs.isRetirementAccount} 
                          onChange={handleChange}
                        />
                        Retirement Account (Tax-Deferred)
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="action-buttons">
                <button className="btn reset-btn" onClick={resetCalculator}>
                  <i className="fas fa-redo"></i>
                  Reset
                </button>
                <button className="btn save-btn" onClick={saveToHistory}>
                  <i className="fas fa-save"></i>
                  Save to History
                </button>
              </div>
            </div>
          )}
<WaldoAd type="square" />
          {/* Scenarios Tab */}
          {activeTab === "scenarios" && result && (
            <div className="projection-content">
              <div className="input-section">
                <h3>
                  <i className="fas fa-chart-line"></i>
                  Scenario Analysis
                </h3>
                <p>Compare different investment scenarios and their impact on net profit</p>
                
                <div className="scenario-list">
                  {scenarioData && scenarioData.map((scenario, index) => (
                    <div key={index} className="scenario-item">
                      <div className="scenario-header">
                        <span className="scenario-name">{scenario.name}</span>
                        <span className={`scenario-outcome ${scenario.netProfit >= 0 ? 'positive' : 'negative'}`}>
                          ${scenario.netProfit.toFixed(2)}
                        </span>
                      </div>
                      <div className="scenario-stats">
                        <div className="scenario-stat">
                          <span>Net Return:</span>
                          <span>{scenario.netReturn.toFixed(2)}%</span>
                        </div>
                        <div className="scenario-stat">
                          <span>Gross Profit:</span>
                          <span>${scenario.grossProfit.toFixed(2)}</span>
                        </div>
                        <div className="scenario-stat">
                          <span>Taxes:</span>
                          <span>${scenario.totalTax.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* History Tab */}
          {activeTab === "history" && (
            <div className="history-content">
              <h3>
                <i className="fas fa-history"></i>
                Recent Net Profit Calculations
              </h3>
              {calculationHistory.length === 0 ? (
                <div className="empty-state">
                  <i className="fas fa-inbox fa-3x"></i>
                  <p>No net profit calculations yet. Start calculating to see your history here.</p>
                </div>
              ) : (
                <div className="history-list">
                  {calculationHistory.map(calc => (
                    <div key={calc.id} className="history-item">
                      <div className="history-header">
                        <span className="history-date">
                          <i className="fas fa-clock"></i>
                          {calc.timestamp}
                        </span>
                        <span className={`history-profit ${calc.result.netProfit >= 0 ? 'positive' : 'negative'}`}>
                          <i className={`fas ${calc.result.netProfit >= 0 ? 'fa-arrow-up' : 'fa-arrow-down'}`}></i>
                          ${calc.result.netProfit.toFixed(2)}
                        </span> 
                      </div>
                      <div className="history-details">
                        <span><i className="fas fa-chart-line"></i> Return: {calc.result.netReturn.toFixed(2)}%</span>
                        <span><i className="fas fa-percentage"></i> Tax Eff: {calc.result.taxEfficiency.toFixed(2)}%</span>
                        <span><i className="fas fa-calendar"></i> {calc.result.holdingPeriod} days</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Side - Results */}
        {(activeTab === "calculator" || activeTab === "scenarios") && (
          <div className="right-panel">
            {activeTab === "calculator" && (
              <div className="results-section">
                <div className="results-header">
                  <h2>
                    <i className="fas fa-chart-bar"></i>
                    Net Profit Analysis
                  </h2>
                  <div className="result-actions">
                    <button className="btn export-btn" onClick={exportToCSV}>
                      <i className="fas fa-file-export"></i>
                      {isMobile ? 'CSV' : 'Export CSV'}
                    </button>
                    <button className="btn print-btn" onClick={printResult}>
                      <i className="fas fa-print"></i>
                      {isMobile ? 'Print' : 'Print'}
                    </button>
                  </div>
                </div>
<WaldoAd type="square" />
                {result ? (
                  <>
                    <div className="results-grid">
                      <div className="result-card highlight">
                        <div className={`result-value ${result.netProfit >= 0 ? 'positive' : 'negative'}`}>
                          ${result.netProfit.toFixed(2)}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-money-bill-wave"></i>
                          Net Profit After Tax
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.netReturn.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-chart-line"></i>
                          Net Return
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.taxEfficiency.toFixed(2)}%
                        </div>
                        <div className="result-label">
                          <i className="fas fa-percentage"></i>
                          Tax Efficiency
                        </div>
                      </div>
                      <div className="result-card">
                        <div className="result-value">
                          {result.isLongTerm ? "Long-term" : "Short-term"}
                        </div>
                        <div className="result-label">
                          <i className="fas fa-clock"></i>
                          Holding Period
                        </div>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <h4>Profit & Loss Breakdown</h4>
                      <div className="detail-row">
                        <span><i className="fas fa-piggy-bank"></i> Initial Investment:</span>
                        <span>${result.initialInvestment.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-chart-line"></i> Gross Profit:</span>
                        <span>${result.grossProfit.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-file-invoice-dollar"></i> Total Taxes:</span>
                        <span>${result.totalTax.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-receipt"></i> Total Expenses:</span>
                        <span>${result.totalExpenses.toFixed(2)}</span>
                      </div>
                      <div className="detail-row highlight">
                        <span><i className="fas fa-money-bill-wave"></i> Net Profit After Tax:</span>
                        <span className={result.netProfit >= 0 ? 'positive' : 'negative'}>
                          ${result.netProfit.toFixed(2)}
                        </span>
                      </div>
                    </div>

                    <div className="detailed-results">
                      <h4>Tax Breakdown</h4>
                      <div className="detail-row">
                        <span><i className="fas fa-flag-usa"></i> Capital Gains Tax:</span>
                        <span>${result.capitalGainsTax.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-coins"></i> Dividend Tax:</span>
                        <span>${result.dividendTax.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-percentage"></i> Interest Tax:</span>
                        <span>${result.interestTax.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-building"></i> State Tax:</span>
                        <span>${result.stateTax.toFixed(2)}</span>
                      </div>
                      <div className="detail-row">
                        <span><i className="fas fa-plus-circle"></i> Net Investment Income Tax:</span>
                        <span>${result.niit.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Profit Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-chart-pie"></i>
                        Profit Distribution
                      </h3>
                      <Doughnut 
                        data={profitBreakdownChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Tax Breakdown Chart */}
                    <div className="chart-container">
                      <h3>
                        <i className="fas fa-file-invoice-dollar"></i>
                        Tax Distribution
                      </h3>
                      <Doughnut 
                        data={taxBreakdownChartData} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: true,
                          plugins: {
                            legend: {
                              position: isMobile ? 'top' : 'bottom'
                            },
                            tooltip: {
                              callbacks: {
                                label: function(context) {
                                  return `$${context.parsed.toFixed(2)}`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>

                    {/* Performance Metrics */}
                    <div className="risk-summary">
                      <h4>Performance Metrics</h4>
                      <div className="risk-cards">
                        <div className="risk-card">
                          <div className="risk-value positive">
                            {result.annualizedReturn.toFixed(2)}%
                          </div>
                          <div className="risk-label">Annualized Return</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value moderate">
                            {result.taxEfficiency.toFixed(2)}%
                          </div>
                          <div className="risk-label">Tax Efficiency</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            {result.holdingDays}
                          </div>
                          <div className="risk-label">Holding Days</div>
                        </div>
                        <div className="risk-card">
                          <div className="risk-value">
                            {(result.taxToProfitRatio * 100).toFixed(2)}%
                          </div>
                          <div className="risk-label">Tax Burden</div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="empty-results">
                    <i className="fas fa-calculator fa-3x"></i>
                    <p>Enter your investment details to see net profit calculations</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === "scenarios" && scenarioData && (
              <div className="projection-results">
                <h3>
                  <i className="fas fa-chart-bar"></i>
                  Scenario Comparison
                </h3>
                <div className="chart-container">
                  <Bar 
                    data={scenarioChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: {
                        legend: {
                          display: true,
                          position: 'top'
                        }
                      },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ticks: {
                            callback: function(value) {
                              return '$' + value.toLocaleString();
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
                <div className="scenario-summary">
                  <h4>Scenario Analysis Summary</h4><br/>
                  <div className="summary-grid">
                    <div className="summary-item">
                      <span>Best Case Net Profit:</span>
                      <span className="positive">${Math.max(...scenarioData.map(s => s.netProfit)).toFixed(2)}</span>
                    </div>
                    <div className="summary-item">
                      <span>Worst Case Net Profit:</span>
                      <span className="negative">${Math.min(...scenarioData.map(s => s.netProfit)).toFixed(2)}</span>
                    </div>
                    <div className="summary-item">
                      <span>Average Net Return:</span>
                      <span>{(scenarioData.reduce((sum, s) => sum + s.netReturn, 0) / scenarioData.length).toFixed(2)}%</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <br/><WaldoAd type="horizontal" />
      {content && content.content}
    </div>
  );
};

export default NetProfitAfterTaxCalculator;